export type DniType = 'V' | 'J' | 'G' | 'C' | 'E' | 'P';

export const DNI_TYPES: { value: DniType; label: string }[] = [
    { value: 'V', label: 'V - Venezolano' },
    { value: 'J', label: 'J - Jurídico (RIF)' },
    { value: 'G', label: 'G - Gobierno' },
    { value: 'C', label: 'C - Comunidad Extranjera' },
    { value: 'E', label: 'E - Extranjero' },
    { value: 'P', label: 'P - Pasaporte' },
];

export interface Customer {
    _id?: string;
    dniType?: DniType | null;
    dniNumber?: string | null;
    name: string;
    email: string;
    phone: string;
    address: string;
    number?: number;
}

/**
 * Get formatted DNI from customer object.
 * Uses dniType/dniNumber fields.
 *
 * Formatting rules by type:
 * - V, E: Use dots as thousand separators (e.g., "V-12.345.678")
 * - J, G, C: Last digit separated with dash, no dots (e.g., "J-12345678-9")
 * - P: No formatting, just the raw number (e.g., "P-ABC123456")
 *
 * @param customer - Customer object or any object with dni fields
 * @returns Formatted DNI string or empty string
 */
export function getFormattedDni(customer: Partial<Customer> | null | undefined): string {
    if (!customer) return '';

    if (customer.dniType && customer.dniNumber) {
        return formatDniByType(customer.dniType, customer.dniNumber);
    }

    return '';
}

/**
 * Format DNI number based on type
 */
function formatDniByType(type: string, number: string): string {
    switch (type) {
        case 'V':
        case 'E':
            // Format with thousand separators (dots)
            const formattedWithDots = number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            return `${type}-${formattedWithDots}`;

        case 'J':
        case 'G':
        case 'C':
            // RIF format: last digit separated with dash, no dots
            if (number.length > 1) {
                const mainPart = number.slice(0, -1);
                const checkDigit = number.slice(-1);
                return `${type}-${mainPart}-${checkDigit}`;
            }
            return `${type}-${number}`;

        case 'P':
            // Passport: no formatting
            return `${type}-${number}`;

        default:
            return `${type}-${number}`;
    }
}
